import {zodResolver} from '@hookform/resolvers/zod';
import {MarketOptIn} from 'puffco-api-axios-client';
import React from 'react';
import {useForm} from 'react-hook-form';
import {Keyboard, Text as NativeText, View} from 'react-native';
import {useSelector} from 'react-redux';
import * as z from 'zod';

import {Button, LoadingButton} from '../../components/Button';
import {ErrorMessage} from '../../components/Form/ErrorMessage';
import {Input} from '../../components/Form/Input';
import {InputContainer} from '../../components/Form/InputContainer';
import {PasswordInput} from '../../components/Form/PasswordInput';
import {ValidatedField} from '../../components/Form/ValidatedField';
import {inputIconProps} from '../../components/Form/util';
import {Separator} from '../../components/Separator';
import {StyledCheckBox} from '../../components/StyledCheckBox';
import {Text} from '../../components/Typography';
import {
  ErrorMessages,
  Messages,
  Strings,
  interpolateWithNode,
  texts,
} from '../../constants/Strings';
import {Screens} from '../../constants/navigation';
import {MailIcon} from '../../icons';
import {marketOptInAccess} from '../../lib/api';
import {isHttpError} from '../../lib/api/apis';
import {useBackPress} from '../../lib/hooks/useBackPress';
import {useLogin} from '../../lib/hooks/useLogin';
import {updateAppFlags} from '../../lib/redux/slices/appFlagsSlice';
import {userSelector} from '../../lib/redux/slices/userSlice';
import {useAppDispatch} from '../../lib/redux/useAppDispatch';
import {responsive} from '../../lib/utils';
import NavigationService from '../../lib/utils/NavigationService';
import {updateUser} from '../../lib/utils/updateUser';
import type {RootStackScreenProps} from '../../navigators/RootStackNavigator';
import {optionsToRoute, toHome} from '../../navigators/util';
import {analytics} from '../../services/analytics';
import {bold, centeredRowStyle, lowercase, underlineText} from '../../styles';
import {LogoHeader, LogoTitle} from '../components';
import {KeyboardAvoidingLayout} from '../components/composable';
import {AuthProps} from './RegisterScreen';
import {SocialAuth} from './components/SocialAuth';
import {authStyles} from './styles';

const lockTime = 15;

interface LoginStatus {
  type?: string;
  remainAttempts?: number;
  unlockTime?: number;
}

const schema = z.object({
  email: z
    .string()
    .nonempty(ErrorMessages.REQUIRED_EMAIL)
    .email(ErrorMessages.INVALID_EMAIL)
    .transform(val => val.trim()),
  password: z
    .string()
    .nonempty(ErrorMessages.REQUIRED_PASSWORD)
    .min(8, ErrorMessages.PASSWORD_MINIMUM_ERROR),
  marketOptIn: z.boolean().default(false),
});

type LoginValues = z.infer<typeof schema>;

export interface Props extends AuthProps {
  token?: string;
}

interface ScreenProps extends RootStackScreenProps<typeof Screens.Login> {}

export const LoginScreen: React.FC<ScreenProps> = ({route, navigation}) => {
  const login = useLogin();
  const dispatch = useAppDispatch();
  const user = useSelector(userSelector);

  const {redirect, email, password, canSkip} = route.params ?? {};

  const defaultValues: LoginValues = {
    email: email ?? '',
    password: password ?? '',
    marketOptIn: false,
  };

  const {
    control,
    handleSubmit,
    getValues,
    setError,
    formState: {isSubmitting, errors},
  } = useForm<LoginValues>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur',
  });

  const onSubmit = React.useCallback(
    async ({email, password, marketOptIn}: LoginValues) => {
      Keyboard.dismiss();

      try {
        await login({email, password});
        analytics.trackEvent('login', {method: 'credentials'});

        if (marketOptIn)
          await marketOptInAccess
            .subscribeToEmail({email})
            .then(() => updateUser(dispatch, {marketOptIn: MarketOptIn.Email}))
            .then(() => analytics.trackEvent('optin', {content_type: 'email'}));
      } catch (error) {
        if (!isHttpError(error)) throw error;

        const status = (error.response?.data ?? {}) as LoginStatus | undefined;

        switch (status?.type) {
          case 'Attempt': {
            if (
              status.remainAttempts &&
              status.remainAttempts < 3 &&
              status.remainAttempts > 0
            ) {
              const warning = interpolateWithNode(texts.screens.login.warning, {
                count: (
                  <NativeText style={bold}>{status.remainAttempts}</NativeText>
                ),
                min: <NativeText style={bold}>{lockTime}</NativeText>,
                attempts: (
                  <NativeText style={lowercase}>
                    {status.remainAttempts > 1
                      ? Strings.ATTEMPTS
                      : Strings.ATTEMPT}
                  </NativeText>
                ),
              });

              setError('root', {
                message: warning as unknown as string,
              });

              return;
            }

            throw new Error(ErrorMessages.INVALID_CREDENTIALS);
          }

          case 'Temporary': {
            const locked = interpolateWithNode(texts.screens.login.locked, {
              here: (
                <Button
                  variant="transparent"
                  as={NativeText}
                  onPress={() => navigation.navigate(Screens.ForgotPassword)}
                  style={[underlineText, bold, lowercase]}>
                  {Strings.Here}
                </Button>
              ),
              min: (
                <NativeText style={bold}>
                  {status.unlockTime ?? lockTime}
                </NativeText>
              ),
            });

            setError('root', {
              message: locked as unknown as string,
            });

            return;
          }

          default:
            throw new Error(
              error.response?.status === 401
                ? ErrorMessages.INVALID_CREDENTIALS
                : ErrorMessages.NETWORK_ERROR,
            );
        }
      }

      dispatch(updateAppFlags({hasSeenNewUsername: !user?.defaultUsername}));

      NavigationService.instance()?.resetRoot({
        index: 0,
        routes: [optionsToRoute(redirect ?? toHome)],
      });
    },
    [login, redirect, user?.defaultUsername],
  );

  useBackPress(() => {
    navigation.goBack();
    return true;
  });

  return (
    <KeyboardAvoidingLayout>
      <View style={[authStyles.innerContainer, authStyles.formContainer]}>
        <LogoHeader>
          <LogoTitle>{Messages.LOGIN_TITLE}</LogoTitle>
        </LogoHeader>

        <View style={authStyles.bodyContainer}>
          <ValidatedField {...{control}} name="email">
            {props => (
              <InputContainer invalid={!!errors.email}>
                <MailIcon {...inputIconProps} />
                <Input
                  keyboardType="email-address"
                  placeholder={Strings.EMAIL_ADDRESS}
                  {...props}
                />
              </InputContainer>
            )}
          </ValidatedField>

          <ValidatedField {...{control}} name="password">
            {props => (
              <PasswordInput
                invalid={!!errors.password}
                placeholder="Password"
                {...props}
              />
            )}
          </ValidatedField>

          <ValidatedField
            {...{control}}
            name="marketOptIn"
            style={authStyles.optInField}>
            {props => (
              <StyledCheckBox {...props} style={authStyles.checkbox}>
                <Text variant="xs">{Messages.MARKET_OPT_IN_PROMP}</Text>
              </StyledCheckBox>
            )}
          </ValidatedField>

          {errors.root?.message && (
            <ErrorMessage>{errors.root?.message}</ErrorMessage>
          )}

          <LoadingButton
            loading={isSubmitting}
            disabled={isSubmitting}
            onPress={handleSubmit(values =>
              onSubmit(values).catch(e => {
                setError('root', {message: e.message});
              }),
            )}>
            {Strings.SIGN_IN}
          </LoadingButton>

          <View style={centeredRowStyle}>
            <Text variant="s">{Messages.NO_ACCOUNT} </Text>

            <Button
              variant="transparent"
              onPress={() =>
                navigation.replace(Screens.Register, {
                  redirect,
                  email: getValues('email'),
                  password: getValues('password'),
                  canSkip,
                })
              }>
              <Text style={underlineText} variant="s">
                {Strings.CREATE_ACCOUNT}
              </Text>
            </Button>
          </View>

          <Button
            variant="transparent"
            onPress={() =>
              navigation.navigate(Screens.ForgotPassword, {
                email: getValues('email'),
              })
            }>
            <Text style={underlineText} variant="s">
              {Messages.FORGOT_PASSWORD}
            </Text>
          </Button>

          <Separator
            style={{marginTop: responsive(24)}}
            text={Messages.OR_CONTINUE_WITH}
          />

          <SocialAuth screen="login" />
        </View>
      </View>
    </KeyboardAvoidingLayout>
  );
};
